'use client';

import React, { useState, useEffect } from 'react';
import { Toaster } from 'sonner';
import WizardHeader from './WizardHeader';
import StepOccasion from './StepOccasion';
import StepBudget from './StepBudget';
import StepGender from './StepGender';
import StepAppearance from './StepAppearance';
import GeneratingScreen from './GeneratingScreen';
import OutfitResult from './OutfitResult';
import ErrorScreen from './ErrorScreen';
import SavedOutfitsCarousel from './SavedOutfitsCarousel';
import OnboardingTour from './OnboardingTour';
import { generateOutfit } from '../lib/gemini';
import type { StyleProfile, SavedOutfitSummary } from '../lib/gemini';
import type { WizardData, OutfitData } from '../lib/types';
import { useSavedOutfits } from '@/lib/hooks/useSavedOutfits';
import { savedOutfitsService } from '@/lib/services/savedOutfitsService';
import type { SavedOutfit } from '@/lib/services/savedOutfitsService';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  trackWizardCompleted,
  trackWizardError,
  trackWizardRetry,
  trackWizardReset,
  trackOccasionSelected,
  trackBudgetSelected,
} from '@/lib/analytics';

const WIZARD_LAST_RUN_KEY = (userId: string) => `atelier_last_wizard_${userId}`;

export type WizardStep = 'occasion' | 'budget' | 'gender' | 'appearance' | 'generating' | 'result' | 'error';

const STEP_ORDER: WizardStep[] = ['occasion', 'budget', 'gender', 'appearance'];

export default function FashionDiscoveryClient() {
  const [currentStep, setCurrentStep] = useState<WizardStep>('occasion');
  const [wizardData, setWizardData] = useState<Partial<WizardData>>({});
  const [outfitData, setOutfitData] = useState<OutfitData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fallbackOutfit, setFallbackOutfit] = useState<OutfitData | null>(null);
  const [lastFinalData, setLastFinalData] = useState<WizardData | null>(null);
  const [showTour, setShowTour] = useState<boolean>(false);
  const [styleProfile, setStyleProfile] = useState<StyleProfile | null>(null);

  const { user } = useAuth();
  const supabase = createClient();

  useEffect(() => {
    if (!localStorage.getItem('atelier_tour_seen')) {
      setShowTour(true);
    }
  }, []);

  // Load last wizard run data for signed-in users to auto-populate fields
  useEffect(() => {
    if (!user) return;
    try {
      const stored = localStorage.getItem(WIZARD_LAST_RUN_KEY(user.id));
      if (stored) {
        const lastRun: WizardData = JSON.parse(stored);
        setWizardData(prev => ({
          budget: lastRun.budget,
          currency: lastRun.currency,
          gender: lastRun.gender,
          appearance: lastRun.appearance,
          ...prev, // don't overwrite if already set (e.g. occasion from URL)
        }));
      }
    } catch {
      // ignore parse errors
    }
  }, [user]);

  // Fetch style profile for signed-in users
  useEffect(() => {
    if (!user) return;
    supabase
      .from('style_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setStyleProfile({
            aesthetics: data.aesthetics ?? [],
            occasions: data.occasions ?? [],
            brandAffinities: data.brand_affinities ?? [],
            sustainableOnly: data.sustainable_only ?? false,
            preOwnedPreferred: data.pre_owned_preferred ?? false,
          });
        }
      });
  }, [user]);

  const { savedOutfits, reload: reloadSaved } = useSavedOutfits();

  const stepIndex = STEP_ORDER.indexOf(currentStep as WizardStep);

  const handleOccasionNext = (occasion: string) => {
    setWizardData(prev => ({ ...prev, occasion }));
    trackOccasionSelected(occasion);
    setCurrentStep('budget');
  };

  const handleBudgetNext = (budget: number, currency: string) => {
    setWizardData(prev => ({ ...prev, budget, currency }));
    trackBudgetSelected(budget, currency);
    setCurrentStep('gender');
  };

  const handleGenderNext = (gender: 'women' | 'men') => {
    setWizardData(prev => ({ ...prev, gender }));
    setCurrentStep('appearance');
  };

  const runGeneration = async (finalData: WizardData) => {
    setCurrentStep('generating');
    setError(null);

    // Build saved outfit history summaries for prompt weighting
    const savedHistory: SavedOutfitSummary[] = savedOutfits.map((s) => ({
      aesthetic: s.outfit.aesthetic,
      keyWords: s.outfit.keyWords,
      occasion: s.wizardData?.occasion,
    }));

    try {
      const result = await generateOutfit(finalData, styleProfile, savedHistory);
      setOutfitData(result);
      setFallbackOutfit(null);
      trackWizardCompleted({
        occasion: finalData.occasion,
        budget: finalData.budget,
        currency: finalData.currency,
        gender: finalData.gender,
      });
      setCurrentStep('result');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate outfit. Please try again.';
      setError(msg);
      trackWizardError({ occasion: finalData.occasion, budget: finalData.budget });
      // Generate fallback outfit synchronously from mock data
      try {
        const { getMockOutfitFallback } = await import('../lib/gemini');
        const fallback = getMockOutfitFallback(finalData);
        setFallbackOutfit(fallback);
      } catch {
        setFallbackOutfit(null);
      }

      setCurrentStep('error');
    }
  };

  const handleAppearanceNext = async (appearance: WizardData['appearance']) => {
    const finalData: WizardData = {
      occasion: wizardData.occasion!,
      budget: wizardData.budget!,
      currency: wizardData.currency!,
      gender: wizardData.gender!,
      appearance,
    };
    setWizardData(finalData);
    setLastFinalData(finalData);

    // Persist last wizard run for signed-in users
    if (user) {
      try {
        localStorage.setItem(WIZARD_LAST_RUN_KEY(user.id), JSON.stringify(finalData));
      } catch {
        // ignore storage errors
      }
    }

    await runGeneration(finalData);
  };

  const handleRetry = async () => {
    if (lastFinalData) {
      trackWizardRetry();
      await runGeneration(lastFinalData);
    }
  };

  const handleBack = () => {
    const idx = STEP_ORDER.indexOf(currentStep as WizardStep);
    if (idx > 0) {
      setCurrentStep(STEP_ORDER[idx - 1]);
    }
  };

  const handleReset = () => {
    setCurrentStep('occasion');
    setWizardData({});
    setOutfitData(null);
    setError(null);
    setFallbackOutfit(null);
    setLastFinalData(null);
    trackWizardReset();
  };

  const handleRemoveSaved = async (id: string) => {
    await savedOutfitsService.remove(id);
    reloadSaved();
  };

  const handleViewSaved = (saved: SavedOutfit) => {
    setOutfitData(saved.outfit);
    setWizardData(saved.wizardData);
    setLastFinalData(saved.wizardData);
    setCurrentStep('result');
  };

  const handleTourComplete = () => {
    localStorage.setItem('atelier_tour_seen', '1');
    setShowTour(false);
  };

  const isWizardStep = STEP_ORDER.includes(currentStep as WizardStep);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF7F2' }}>
      <Toaster position="bottom-right" richColors />

      {showTour && <OnboardingTour onComplete={handleTourComplete} />}

      {/* Header — shown only during wizard steps */}
      {isWizardStep && (
        <WizardHeader
          currentStep={currentStep as WizardStep}
          stepIndex={stepIndex}
          totalSteps={STEP_ORDER.length}
          onBack={stepIndex > 0 ? handleBack : undefined}
        />
      )}

      {/* Saved outfits quick-access carousel — shown on wizard steps when outfits are saved */}
      {isWizardStep && savedOutfits.length > 0 && (
        <SavedOutfitsCarousel
          savedOutfits={savedOutfits}
          onRemove={handleRemoveSaved}
          onViewOutfit={handleViewSaved}
        />
      )}

      {/* Step Content */}
      <main>
        {currentStep === 'occasion' && (
          <StepOccasion
            initialValue={wizardData.occasion}
            onNext={handleOccasionNext}
          />
        )}
        {currentStep === 'budget' && (
          <StepBudget
            initialBudget={wizardData.budget}
            initialCurrency={wizardData.currency}
            onNext={handleBudgetNext}
          />
        )}
        {currentStep === 'gender' && (
          <StepGender
            initialValue={wizardData.gender}
            onNext={handleGenderNext}
          />
        )}
        {currentStep === 'appearance' && (
          <StepAppearance
            gender={wizardData.gender!}
            initialValue={wizardData.appearance}
            onNext={handleAppearanceNext}
            error={error}
          />
        )}
        {currentStep === 'generating' && (
          <GeneratingScreen occasion={wizardData.occasion} />
        )}
        {currentStep === 'result' && outfitData && (
          <OutfitResult
            outfit={outfitData}
            wizardData={wizardData as WizardData}
            onReset={handleReset}
          />
        )}
        {currentStep === 'error' && (
          <ErrorScreen
            errorMessage={error ?? 'An unexpected error occurred. Please try again.'}
            fallbackOutfit={fallbackOutfit}
            wizardData={wizardData}
            onRetry={handleRetry}
            onReset={handleReset}
          />
        )}
      </main>
    </div>
  );
}